import {VehicleProject, VehicleProjectMaster} from 'types';
import {TFunction} from 'react-i18next';
import {
	renderHeaderXL,
	createZIPXL,
	addXLTable,
	addHTMLImagesAsLinks,
	renderRichtText,
	renderArrayXL,
	renderDateXL,
	renderHTMLStringXL,
	renderStringArrayXL,
	XLColumn,
	mergeColumnsExcel,
	getColumnAlphabetExcel,
} from './ExcelExportCommon';
import Excel from 'exceljs';
import i18n from 'i18n/i18n';
import {
	AssignedRequirementWithID,
	resolveVehicleProjects,
} from 'features/VehicleProjects/AssignedEntities/AssignedRequirementColumns';
import {
	getBodyWorkVariants,
	getDriveVariants,
	getEarliestPosEtVsi,
	getProjectName,
	getTechnologyVariants,
} from 'features/VehicleProjects/components/vehicleProjectColumns';
import {
	getColumnsXLRender,
	renderRegulationNumberXL,
	renderRegulationVersionXL,
} from './XLRequirementsPage';
import {InfinityListColumn} from '../InfinityList';

interface IAssignedRequirementList {
	(
		baselineName: string | undefined,
		entityListItems: AssignedRequirementWithID[],
		columns: InfinityListColumn[],
		dictVehicleProjects: Record<string, VehicleProject>,
		vehicleProjectMaster: VehicleProjectMaster | undefined,
		t: TFunction,
	): Promise<void>;
}

const tOptions = {
	ns: 'features/vehicleprojects',
	keyPrefix: 'AssignedRequirementsFields',
};

const tOptionsRelevanz = {
	ns: 'features/vehicleprojects',
	keyPrefix: 'ProjectRelevantFields',
};

const getColumnsXLRenderAssignment = (t: TFunction) => [
	{
		key: 'vehicleProjects',
		xlRender: (iAssignedReq: AssignedRequirementWithID) =>
			(iAssignedReq.vehicleProjects ?? [])
				.map(vp => getProjectName(vp, i18n))
				.join(';'),
	},
	{
		key: 'isProjectRelevant',
		xlRender: (iAssignedReq: AssignedRequirementWithID) =>
			iAssignedReq.isProjectRelevant
				? t('Yes', tOptionsRelevanz)
				: t('No', tOptionsRelevanz),
	},
	{
		key: 'projectRelevantComments',
		xlRender: (iAssignedReq: AssignedRequirementWithID) =>
			iAssignedReq.projectRelevantComments ?? '',
	},

	{
		key: 'addedAutomatically',
		xlRender: (iAssignedReq: AssignedRequirementWithID) =>
			iAssignedReq.addedAutomatically
				? t('true', tOptions)
				: t('false', tOptions),
	},
	{
		key: 'addReason',
		xlRender: (iAssignedReq: AssignedRequirementWithID) =>
			iAssignedReq.addReason === 'AutomaticAssignment'
				? t('AutomaticAssignment', tOptions)
				: iAssignedReq.addReason,
	},
];

export const handleClickAssignedRequirements: IAssignedRequirementList = async (
	baselineName,
	entityListItems,
	columns,
	dictVehicleProjects,
	vehicleProjectMaster,
	t,
) => {
	const filtRequirement = entityListItems.map(x =>
		resolveVehicleProjects(x, dictVehicleProjects),
	);

	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];
	const arrTables: string[] = [];

	const getHeadingVehProject = (rawHeading: string) => {
		return t(rawHeading, {
			ns: 'features/vehicleprojects',
		});
	};

	const workbook = new Excel.Workbook();
	const sheet = workbook.addWorksheet(
		'TRACE_' + getHeadingVehProject('AssignedRequirements'),
	);

	const columnsXLRender = [
		...getColumnsXLRenderAssignment(t),
		...getColumnsXLRender(t),
	];
	const columnsXL: XLColumn[] = mergeColumnsExcel(columns, columnsXLRender);

	const arrColWidth = columnsXL.map(x => x.xlWidth ?? 25);
	const strPageName = getHeadingVehProject('VehicleProjects');

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		getHeadingVehProject('ModelSeries'),
		vehicleProjectMaster?.modelSeries ?? '',
	);

	row.getCell('C').value = renderRichtText(
		getHeadingVehProject('Generation'),
		vehicleProjectMaster?.generation ?? '',
	);

	if (baselineName) {
		row.getCell('D').value = renderRichtText('Baseline', baselineName);
	}

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	const derivates = renderStringArrayXL(
		(vehicleProjectMaster?.derivates ?? []).filter(x => x.trim() !== ''),
	);
	const sOP = renderDateXL(vehicleProjectMaster?.sopVsi);
	const eOP = renderDateXL(vehicleProjectMaster?.eopVsi);
	const lastEvaluated = renderDateXL(vehicleProjectMaster?.lastEvaluated);

	row.getCell('B').value = renderRichtText(
		getHeadingVehProject('Derivative'),
		derivates,
	);
	row.getCell('C').value = renderRichtText(
		getHeadingVehProject('EarliestSOP'),
		sOP,
	);
	row.getCell('D').value = renderRichtText(
		getHeadingVehProject('LatestEOP'),
		eOP,
	);
	row.getCell('E').value = renderRichtText(
		getHeadingVehProject('LastEvaluated'),
		lastEvaluated,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	const vehicleProjects: VehicleProject[] = Object.values(dictVehicleProjects);
	const bwvr = renderStringArrayXL(getBodyWorkVariants(vehicleProjects));
	const dvr = renderStringArrayXL(getDriveVariants(vehicleProjects));
	const pos = getEarliestPosEtVsi(vehicleProjects) ?? '-';
	const technology = renderStringArrayXL(
		getTechnologyVariants(vehicleProjects),
	);

	row.getCell('B').value = renderRichtText(
		getHeadingVehProject('BodyworkVariants'),
		bwvr,
	);
	row.getCell('C').value = renderRichtText(
		getHeadingVehProject('DriveVariants'),
		dvr,
	);
	row.getCell('D').value = renderRichtText(t('EarliestPOS'), pos);
	row.getCell('E').value = renderRichtText(
		getHeadingVehProject('Technology'),
		technology,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		getHeadingVehProject('VehicleProjects'),
		vehicleProjects.map(x => getProjectName(x, i18n)).join(';\n'),
	);

	intRowCount++;
	intRowCount++;
	row = sheet.getRow(intRowCount);

	row.font = {
		bold: true,
		name: 'Porsche Next TT',
		size: 10,
		color: {argb: 'FFFFFFFF'},
	};

	const tableName = 'tableRequirements';

	addXLTable(tableName, intRowCount, sheet, columnsXL);

	const table = sheet.getTable(tableName);

	intRowCount++;
	const tempDivElement = document.createElement('div');

	if (filtRequirement.length === 0) {
		table.addRow(new Array(columnsXL.length).fill(''));
		intRowCount++;
	}

	for (let ii = 0; ii < filtRequirement.length; ii++) {
		const iAssignedReq = filtRequirement[ii];

		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};

		columnsXL.forEach((col, index) => {
			const colAlpha = getColumnAlphabetExcel(index + 1);
			if (col?.xlFontStyle) {
				row.getCell(colAlpha).style.font = col.xlFontStyle;
			}
		});
		const renderedXLRow = columnsXL.map(x => {
			const xlRenderFnDef = () => '';
			const xlRenderFn = x.xlRender ?? xlRenderFnDef;
			return xlRenderFn(iAssignedReq);
		});

		table.addRow(renderedXLRow);

		intRowCount++;

		tempDivElement.innerHTML = iAssignedReq.definition
			? '<div>' + iAssignedReq.definition + '</div>'
			: '<div></div>';

		const indexDefintion = columnsXL.findIndex(x => x.key == 'definition') + 1;
		intRowCount = addHTMLImagesAsLinks(
			intRowCount,
			iAssignedReq.requirementId as string,
			arrImagesName,
			arrImagesUri,
			getColumnAlphabetExcel(indexDefintion + 1),
			indexDefintion,
			tempDivElement,
			sheet,
			table,
			t,
		);
	}

	table.commit();

	const strZipName = t('RequirementsHeader', {ns: 'features/requirements'});
	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		[],
	);
};
